import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Users,
    CheckCircle2,
    Hourglass,
    XCircle,
    Search,
    SlidersHorizontal,
    Share2,
    Globe,
    Eye,
    Check,
    X,
    ChevronLeft,
    ChevronRight,
    Mail,
    Loader2,
    RefreshCw,
    FileText,
    Inbox,
    ArrowLeft,
} from "lucide-react";
import ReachableChannelsCard from "./ReachableChannelsCard";
import { GROWTHOS_BASE } from "../config/api";  

/* ==========================================================
   API WIRING
   Mirrors src/config/api.js's own convention (see
   backend/local_dev_app.py): every main.py route — /leads,
   /leads/{id}/profile-status, etc. — is reached under a
   "/growthos" prefix, both locally (via local_dev_app.py's
   mount) and in production. If this project already has a
   shared axios/fetch client (e.g. src/config/api.js or an
   apiClient helper), swap the three calls below for that
   instead of this local `apiFetch` — this file only assumes
   a REACT_APP_API_BASE_URL env var + a Bearer token in
   localStorage under "growthos_token", same key Sidebar.js
   already reads/clears on logout.
========================================================== */
//const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const GROWTHOS = `${GROWTHOS_BASE}`;

// isAdmin picks the SuperAdmin session token first (same fallback
// Superadmin.js already uses elsewhere: growthos_admin_token, falling
// back to growthos_token) — the /admin/leads/... endpoints below are
// unauthenticated for now (see main.py), so this header is a no-op
// until real SuperAdmin auth lands, but sending it now means nothing
// else needs to change here when it does.
const apiFetch = async (path, options = {}, isAdmin = false) => {
    const token = isAdmin
        ? localStorage.getItem("growthos_admin_token") || localStorage.getItem("growthos_token")
        : localStorage.getItem("growthos_token");
    const res = await fetch(`${GROWTHOS}${path}`, {
        ...options,
        headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(options.headers || {}),
        },
    });
    let body = null;
    try {
        body = await res.json();
    } catch {
        // no JSON body — leave as null
    }
    if (!res.ok) {
        throw new Error(body?.detail || body?.message || `Request failed (${res.status})`);
    }
    return body;
};

// company mode: GET /leads/approvals — scoped server-side (get_current_company)
// to just the logged-in company's own leads.
// admin mode: GET /admin/leads/approvals — the UNION of every company's own
// Approvals queue (see fetch_all_approval_leads in main.py), each row tagged
// with company_name. If Company A has 1 pending lead and Company B has 2,
// SuperAdmin sees all 3 here; each company still only ever sees its own.
const fetchLeads = (isAdmin = false) =>
    apiFetch(isAdmin ? "/admin/leads/approvals" : "/leads/approvals", {}, isAdmin);

const setLeadProfileStatus = (leadId, profile_status, isAdmin = false) =>
    apiFetch(
        isAdmin ? `/admin/leads/${leadId}/profile-status` : `/leads/${leadId}/profile-status`,
        { method: "POST", body: JSON.stringify({ profile_status }) },
        isAdmin
    );
// Mints this lead's registration_token server-side and returns the
// {FRONTEND_BASE_URL}/register?lead_token=... link to embed in the
// invite send — see POST /leads/{id}/registration-link in main.py.
// This is what lets a later /company-registrations submission be
// matched back to this exact lead (email is only the fallback).
const createRegistrationLink = (leadId) =>
    apiFetch(`/leads/${leadId}/registration-link`, { method: "POST" });

// The REGISTRANT's own uploaded documents (company_documents table —
// populated at registration Step 4, and/or added later from the AI
// Knowledge Base tab). Scoped server-side to this exact lead — see
// GET /leads/{id}/registered-company-documents in main.py — so this
// company only ever sees another company's documents for a lead it
// actually invited and that lead actually completed registration.
const fetchLeadRegistrationDocuments = (leadId) =>
    apiFetch(`/leads/${leadId}/registered-company-documents`);

const formatFileSize = (bytes) => {
    const n = Number(bytes) || 0;
    if (n <= 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/* ==========================================================
   Maps a raw /leads row (Lead model in main.py) to what this
   table needs. profile_status arrives as one of:
   "interested" (default) | "profile_complete" | "rejected".
========================================================== */
const toRow = (lead) => ({
    id: lead.id,
    name: lead.user_name || "—",
    email: lead.user_email || "—",
    source: lead.lead_source || "—",
    // Only ever populated by GET /admin/leads/approvals (the SuperAdmin
    // aggregated view) — company-scoped GET /leads/approvals never sets
    // this, since a company already knows it's looking at its own leads.
    companyName: lead.company_name || "—",
    website: lead.web_url || "",
    industry: lead.industry || "-",
    profileStatus: lead.profile_status || "interested",
    // Server-confirmed — set once this lead's registration-page submit
    // was matched back to them (by token, or the email fallback), not
    // just "we sent them an invite". null/undefined = not registered yet.
    registeredAt: lead.registered_at || null,
    // Carried through as-is so the invite modal can hand this row straight
    // to ReachableChannelsCard, which expects a full lead object (same
    // shape as CustomerDataContext rows: user_name, user_email,
    // user_mobile_number, country, interest_status, id).
    raw: {
        id: lead.id,
        user_name: lead.user_name || "",
        user_email: lead.user_email || "",
        user_mobile_number: lead.user_mobile_number || "",
        country: lead.country || "",
        interest_status: lead.interest_status || "pending",
    },
});

const initials = (name) =>
    (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";

const StatCard = ({ icon: Icon, label, value, sublabel, tone }) => {
    const tones = {
        violet: "bg-violet-500/15 text-violet-400 border-violet-500/20",
        green: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
        amber: "bg-amber-500/15 text-amber-400 border-amber-500/20",
        red: "bg-red-500/15 text-red-400 border-red-500/20",
    };
    return (
        <div className="flex items-center gap-3 rounded-2xl border border-orange-600/10 bg-white/[0.03] p-4 sm:p-5 min-w-0">
            <div className={`h-11 w-11 shrink-0 rounded-xl border flex items-center justify-center ${tones[tone]}`}>
                <Icon size={20} />
            </div>
            <div className="min-w-0">
                <p className="text-[11px] sm:text-xs text-gray-500 truncate">{label}</p>
                <p className="text-xl sm:text-2xl font-bold text-white leading-tight">{value}</p>
                <p className="text-[11px] text-gray-500 truncate">{sublabel}</p>
            </div>
        </div>
    );
};

const STATUS_LABEL = {
    interested: "Interested",
    profile_complete: "Profile Complete",
    rejected: "Rejected",
};

const StatusBadge = ({ status }) => {
    const styles = {
        interested: "bg-amber-500/15 text-amber-400 border-amber-500/20",
        profile_complete: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
        rejected: "bg-red-500/15 text-red-400 border-red-500/20",
    };
    return (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold border ${styles[status]}`}>
            {STATUS_LABEL[status] || status}
        </span>
    );
};

// mode="company" (default): the normal per-company Approvals page —
// only ever sees/actions its own leads.
// mode="admin": SuperAdmin's aggregated Approvals view — sees the union
// of every company's pending leads (tagged with which company each is
// from) and can approve/reject any of them. Send Invite / Doc stay
// hidden here since those two actions are company-scoped by design
// (they mint/read THAT company's own invite link and documents).
const CompanyApproval = ({ mode = "company", onBack } = {}) => {
    const navigate = useNavigate();
    const isAdmin = mode === "admin";
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actioningId, setActioningId] = useState(null);

    const [search, setSearch] = useState("");
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState([]);
    const [invited, setInvited] = useState([]);
    // Row whose "Send Invite" was clicked — its raw lead object is handed
    // to ReachableChannelsCard so the invite modal shows Email/SMS/WhatsApp/Call.
    const [inviteRow, setInviteRow] = useState(null);
    // The {FRONTEND_BASE_URL}/register?lead_token=... link minted for
    // inviteRow (see createRegistrationLink) — null while it's loading.
    const [inviteLink, setInviteLink] = useState(null);
    const [inviteLinkError, setInviteLinkError] = useState(null);

    // Row whose "Doc" eye icon was clicked — the picker/viewer modal below
    // shows THAT lead's own registration documents, fetched fresh per row
    // (not cached — a different row means a different registrant, and a
    // different company's documents).
    const [docsRow, setDocsRow] = useState(null);
    const [companyDocs, setCompanyDocs] = useState([]);
    const [docsLoading, setDocsLoading] = useState(false);
    const [docsError, setDocsError] = useState(null);
    // true once we know this lead hasn't completed registration yet — a
    // distinct state from "loaded, zero documents uploaded".
    const [docsNotRegistered, setDocsNotRegistered] = useState(false);

    const loadLeads = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchLeads(isAdmin);
            const rows = (res?.data || []).map(toRow);
            setRequests(rows);
        } catch (err) {
            setError(err.message || "Could not load approval requests");
        } finally {
            setLoading(false);
        }
    }, [isAdmin]);

    useEffect(() => {
        loadLeads();
    }, [loadLeads]);

    const stats = useMemo(() => {
        const total = requests.length;
        const approved = requests.filter((r) => r.profileStatus === "profile_complete").length;
        const pending = requests.filter((r) => r.profileStatus === "interested").length;
        const rejected = requests.filter((r) => r.profileStatus === "rejected").length;
        return { total, approved, pending, rejected };
    }, [requests]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return requests;
        return requests.filter(
            (r) =>
                r.name.toLowerCase().includes(q) ||
                r.email.toLowerCase().includes(q) ||
                r.source.toLowerCase().includes(q) ||
                r.industry.toLowerCase().includes(q)
        );
    }, [requests, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const pageRows = filtered.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);

    const actionOn = async (id, status) => {
        setActioningId(id);
        // Optimistic update so the row/badge/stat cards feel instant —
        // rolled back below if the server call fails.
        const prev = requests;
        setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, profileStatus: status } : r)));
        try {
            await setLeadProfileStatus(id, status, isAdmin);
        } catch (err) {
            setRequests(prev);
            setError(err.message || "Could not update this lead — please try again");
        } finally {
            setActioningId(null);
        }
    };

    const openInvite = async (row) => {
        setInviteRow(row);
        setInviteLink(null);
        setInviteLinkError(null);
        try {
            const res = await createRegistrationLink(row.id);
            setInviteLink(res?.link || null);
        } catch (err) {
            // Non-fatal — ReachableChannelsCard can still send an invite
            // without the link; that submission just falls back to the
            // email match on the backend instead of the exact token match.
            setInviteLinkError(err.message || "Could not create a registration link for this lead");
        }
    };

    // Opens the Doc viewer for a given lead row — fetches THAT lead's own
    // registration documents fresh every time (each row is a different
    // registrant, so nothing here can be cached across rows).
    const openDocs = async (row) => {
        setDocsRow(row);
        setCompanyDocs([]);
        setDocsNotRegistered(false);
        setDocsLoading(true);
        setDocsError(null);
        try {
            const res = await fetchLeadRegistrationDocuments(row.id);
            if (res?.message === "not_registered") {
                setDocsNotRegistered(true);
            } else {
                setCompanyDocs(res?.data || []);
            }
        } catch (err) {
            setDocsError(err.message || "Could not load documents");
        } finally {
            setDocsLoading(false);
        }
    };

    // Opens one document in a new tab. file_url comes back from the API as
    // a root-relative path (e.g. "/uploads/knowledge/xxx.pdf") that lives
    // under main.py's own app — so it needs the same "/growthos" prefix
    // every other call in this file already uses, not just API_BASE.
    const viewDocument = (doc) => {
        if (!doc?.file_url) return;
        window.open(`${GROWTHOS}${doc.file_url}`, "_blank", "noopener,noreferrer");
    };

    // Fired by ReachableChannelsCard only after an actual send/call
    // succeeds (email/SMS/WhatsApp sent, or the dialer was opened) —
    // this is what actually flips the button to "Invited".
    const handleInviteSent = (leadId) => {
        setInvited((prev) => [...new Set([...prev, leadId])]);
    };

    const toggleSelected = (id) =>
        setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const allChecked = pageRows.length > 0 && pageRows.every((r) => selected.includes(r.id));
    const toggleAll = () =>
        setSelected((prev) =>
            allChecked ? prev.filter((id) => !pageRows.some((r) => r.id === id)) : [...new Set([...prev, ...pageRows.map((r) => r.id)])]
        );

    const startIdx = filtered.length === 0 ? 0 : (page - 1) * pageSize + 1;
    const endIdx = Math.min(page * pageSize, filtered.length);
    // checkbox + Lead + Source + Profile Status + Website + Industry + Action = 7,
    // +1 for Company (admin only), +2 for Send Invite/Doc (company only).
    const colCount = 7 + (isAdmin ? 1 : 2);

    return (
        <div className="h-full overflow-y-auto pr-1 pb-8">
            {(!isAdmin || onBack) && (
                <button
                    type="button"
                    onClick={() => (onBack ? onBack() : navigate("/app/dashboard-preview"))}
                    className="shrink-0 self-start mb-4 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium bg-white/5 border border-white/10 text-gray-300 hover:text-orange-400 hover:border-orange-600/40 hover:bg-orange-600/10 transition"
                >
                    <ArrowLeft size={15} /> Back
                </button>
            )}
            <div className="flex items-start justify-between gap-3 mb-5">
                <div>
                    <h2 className="text-xl sm:text-2xl font-extrabold text-white">Approvals</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {isAdmin
                            ? "Every company's pending profile requests, in one place"
                            : "Review and action incoming profile requests"}
                    </p>
                </div>
                <button
                    onClick={loadLeads}
                    disabled={loading}
                    className="shrink-0 flex items-center gap-1.5 text-xs font-semibold text-gray-400 border border-orange-600/20 rounded-lg px-3 py-2 hover:bg-white/5 hover:text-orange-500 transition disabled:opacity-40"
                >
                    <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
                    Refresh
                </button>
            </div>

            {error && (
                <div className="mb-4 rounded-xl border border-red-500/20 bg-red-500/10 text-red-400 text-sm px-4 py-3">
                    {error}
                </div>
            )}

            {/* ================= STAT CARDS ================= */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
                <StatCard icon={Users} label="Total Requests" value={stats.total} sublabel="All pending requests" tone="violet" />
                <StatCard icon={CheckCircle2} label="Approved" value={stats.approved} sublabel="This month" tone="green" />
                <StatCard icon={Hourglass} label="Pending" value={stats.pending} sublabel="Awaiting review" tone="amber" />
                <StatCard icon={XCircle} label="Rejected" value={stats.rejected} sublabel="This month" tone="red" />
            </div>

            {/* ================= CONTROLS ================= */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                    <span>Show</span>
                    <select
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setPage(1);
                        }}
                        className="bg-[#111111] border border-orange-600/20 text-white text-sm rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-orange-500"
                    >
                        {[10, 25, 50].map((n) => (
                            <option key={n} value={n}>
                                {n}
                            </option>
                        ))}
                    </select>
                    <span>entries</span>
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:w-64">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                        <input
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                            placeholder="Search..."
                            className="w-full bg-[#111111] border border-orange-600/20 text-white text-sm rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:border-orange-500 placeholder:text-gray-600"
                        />
                    </div>
                    <button className="shrink-0 flex items-center gap-1.5 text-sm text-gray-300 border border-orange-600/20 rounded-lg px-3 py-2 hover:bg-white/5 hover:text-orange-500 transition">
                        <SlidersHorizontal size={14} />
                        Filter
                    </button>
                </div>
            </div>

            {/* ================= TABLE ================= */}
            <div className="rounded-2xl border border-orange-600/10 bg-white/[0.02] overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[900px] text-sm">
                        <thead>
                            <tr className="text-[11px] uppercase tracking-wider text-gray-500 border-b border-orange-600/10">
                                <th className="px-4 py-3 text-left w-10">
                                    <input
                                        type="checkbox"
                                        checked={allChecked}
                                        onChange={toggleAll}
                                        className="accent-orange-500"
                                    />
                                </th>
                                <th className="px-4 py-3 text-left">Lead</th>
                                {isAdmin && <th className="px-4 py-3 text-left">Company</th>}
                                <th className="px-4 py-3 text-left">Source</th>
                                <th className="px-4 py-3 text-left">Profile Status</th>
                                <th className="px-4 py-3 text-left">Website</th>
                                <th className="px-4 py-3 text-left">Industry</th>
                                {!isAdmin && <th className="px-4 py-3 text-left">Send Invite</th>}
                                {!isAdmin && <th className="px-4 py-3 text-left">Doc</th>}
                                <th className="px-4 py-3 text-left">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={colCount} className="px-4 py-10 text-center text-gray-500">
                                        <span className="inline-flex items-center gap-2">
                                            <Loader2 size={16} className="animate-spin" />
                                            Loading approval requests…
                                        </span>
                                    </td>
                                </tr>
                            )}

                            {!loading &&
                                pageRows.map((r) => {
                                    const isComplete = r.profileStatus === "profile_complete";
                                    const isBusy = actioningId === r.id;
                                    return (
                                        <tr key={r.id} className="border-b border-orange-600/5 hover:bg-white/[0.03] transition">
                                            <td className="px-4 py-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selected.includes(r.id)}
                                                    onChange={() => toggleSelected(r.id)}
                                                    className="accent-orange-500"
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div className="h-9 w-9 shrink-0 rounded-full bg-orange-500/15 border border-orange-500/30 text-orange-400 flex items-center justify-center font-semibold text-xs">
                                                        {initials(r.name)}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-white font-medium truncate">{r.name}</p>
                                                        <p className="text-gray-500 text-xs truncate">{r.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            {isAdmin && (
                                                <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{r.companyName}</td>
                                            )}
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="inline-flex items-center gap-1.5 text-gray-300">
                                                    <Share2 size={13} className="text-orange-500" />
                                                    {r.source}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <StatusBadge status={r.profileStatus} />
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                {r.website ? (
                                                    <a
                                                        href={r.website}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="inline-flex items-center gap-1.5 text-gray-400 hover:text-orange-500 transition"
                                                    >
                                                        <Globe size={13} />
                                                        {r.website.replace("https://", "")}
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-600">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{r.industry}</td>
                                            {!isAdmin && (
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <button
                                                        onClick={() => openInvite(r)}
                                                        disabled={!!r.registeredAt}
                                                        className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 border transition disabled:cursor-default ${
                                                            r.registeredAt
                                                                ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10"
                                                                : invited.includes(r.id)
                                                                ? "border-amber-500/30 text-amber-400 bg-amber-500/10"
                                                                : "border-orange-500/40 text-orange-400 hover:bg-orange-500/10"
                                                        }`}
                                                        title={r.registeredAt ? `Registered on ${new Date(r.registeredAt).toLocaleDateString()}` : undefined}
                                                    >
                                                        <Mail size={13} />
                                                        {r.registeredAt ? "Registered" : invited.includes(r.id) ? "Invited" : "Send Invite"}
                                                    </button>
                                                </td>
                                            )}
                                            {!isAdmin && (
                                                <td className="px-4 py-3">
                                                    <button
                                                        onClick={() => openDocs(r)}
                                                        className="h-8 w-8 rounded-lg border border-orange-600/20 flex items-center justify-center text-gray-400 hover:text-orange-500 hover:bg-white/5 transition"
                                                        title="View documents"
                                                    >
                                                        <Eye size={14} />
                                                    </button>
                                                </td>
                                            )}
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => actionOn(r.id, "profile_complete")}
                                                        disabled={isBusy || isComplete}
                                                        className="h-8 w-8 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 flex items-center justify-center hover:bg-emerald-500/20 transition disabled:opacity-40"
                                                        title="Approve"
                                                    >
                                                        <Check size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => actionOn(r.id, "rejected")}
                                                        disabled={isBusy || r.profileStatus === "rejected"}
                                                        className="h-8 w-8 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 flex items-center justify-center hover:bg-red-500/20 transition disabled:opacity-40"
                                                        title="Reject"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}

                            {!loading && pageRows.length === 0 && (
                                <tr>
                                    <td colSpan={colCount} className="px-4 py-10 text-center text-gray-500">
                                        No approval requests found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* ================= FOOTER / PAGINATION ================= */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-orange-600/10">
                    <p className="text-xs text-gray-500">
                        Showing {startIdx} to {endIdx} of {filtered.length} entries
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="h-8 w-8 rounded-lg border border-orange-600/20 flex items-center justify-center text-gray-400 disabled:opacity-30 hover:bg-white/5 hover:text-orange-500 transition"
                        >
                            <ChevronLeft size={14} />
                        </button>
                        <span className="h-8 min-w-[2rem] px-2 rounded-lg bg-orange-500 text-white text-xs font-semibold flex items-center justify-center">
                            {page}
                        </span>
                        <button
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="h-8 w-8 rounded-lg border border-orange-600/20 flex items-center justify-center text-gray-400 disabled:opacity-30 hover:bg-white/5 hover:text-orange-500 transition"
                        >
                            <ChevronRight size={14} />
                        </button>
                    </div>
                </div>
            </div>

            {/* ================= SEND INVITE MODAL ================= */}
            {inviteRow && (
                <div className="fixed inset-0 z-[1997] flex items-center justify-center bg-black/70 px-4">
                    <div className="bg-[#0d0d10] border border-black/60 rounded-2xl p-4 max-w-md w-full max-h-[85vh] shadow-[0_10px_40px_-10px_rgba(0,0,0,0.6)] flex flex-col">
                        <div className="flex items-center justify-between mb-3 shrink-0">
                            <div>
                                <p className="text-white font-bold text-sm">Send Invite</p>
                                <p className="text-xs text-gray-500">{inviteRow.name}</p>
                            </div>
                            <button
                                onClick={() => setInviteRow(null)}
                                className="text-gray-400 hover:text-white"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        {inviteLinkError && (
                            <p className="mb-3 shrink-0 text-[11px] text-amber-400">
                                Couldn't create a tracked registration link ({inviteLinkError}) — the invite can
                                still be sent, but the registration will only be matched back to this lead by
                                email.
                            </p>
                        )}
                        <div className="flex-1 min-h-0">
                            <ReachableChannelsCard
                                lead={inviteRow.raw}
                                registrationLink={inviteLink}
                                onSent={() => handleInviteSent(inviteRow.id)}
                                variant="approval"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* ================= VIEW DOCUMENTS MODAL ================= */}
            {docsRow && (
                <div className="fixed inset-0 z-[1997] flex items-center justify-center bg-black/70 px-4">
                    <div className="bg-[#0d0d10] border border-black/60 rounded-2xl p-4 max-w-md w-full max-h-[85vh] shadow-[0_10px_40px_-10px_rgba(0,0,0,0.6)] flex flex-col">
                        <div className="flex items-center justify-between mb-3 shrink-0">
                            <div>
                                <p className="text-white font-bold text-sm">Documents</p>
                                <p className="text-xs text-gray-500">For {docsRow.name}</p>
                            </div>
                            <button
                                onClick={() => setDocsRow(null)}
                                className="text-gray-400 hover:text-white"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto -mr-1 pr-1">
                            {docsLoading && (
                                <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-10">
                                    <Loader2 size={16} className="animate-spin" />
                                    Loading documents…
                                </div>
                            )}

                            {!docsLoading && docsError && (
                                <div className="rounded-xl border border-red-500/20 bg-red-500/10 text-red-400 text-sm px-4 py-3">
                                    {docsError}
                                </div>
                            )}

                            {!docsLoading && !docsError && docsNotRegistered && (
                                <div className="flex flex-col items-center gap-2 text-center text-gray-500 py-10">
                                    <Hourglass size={22} className="text-gray-600" />
                                    <p className="text-sm">{docsRow.name} hasn't registered yet.</p>
                                    <p className="text-xs text-gray-600">
                                        Documents will show up here once they complete registration
                                        through their invite link.
                                    </p>
                                </div>
                            )}

                            {!docsLoading && !docsError && !docsNotRegistered && companyDocs.length === 0 && (
                                <div className="flex flex-col items-center gap-2 text-center text-gray-500 py-10">
                                    <Inbox size={22} className="text-gray-600" />
                                    <p className="text-sm">No documents uploaded yet.</p>
                                    <p className="text-xs text-gray-600">
                                        {docsRow.name} didn't attach any documents during registration.
                                    </p>
                                </div>
                            )}

                            {!docsLoading && !docsError && !docsNotRegistered && companyDocs.length > 0 && (
                                <div className="space-y-2">
                                    {companyDocs.map((doc) => (
                                        <div
                                            key={doc.id}
                                            className="flex items-center justify-between gap-3 rounded-xl border border-orange-600/10 bg-white/[0.03] px-3 py-2.5"
                                        >
                                            <div className="flex min-w-0 items-center gap-2.5">
                                                <div className="h-8 w-8 shrink-0 rounded-lg bg-orange-500/10 text-orange-400 flex items-center justify-center">
                                                    <FileText size={14} />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm text-white truncate">{doc.file_name}</p>
                                                    <p className="text-[11px] text-gray-500 truncate">
                                                        {[doc.document_type, formatFileSize(doc.file_size)]
                                                            .filter(Boolean)
                                                            .join(" · ") || "Uploaded document"}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => viewDocument(doc)}
                                                className="shrink-0 h-8 w-8 rounded-lg border border-orange-600/20 flex items-center justify-center text-gray-400 hover:text-orange-500 hover:bg-white/5 transition"
                                                title={`View ${doc.file_name}`}
                                            >
                                                <Eye size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanyApproval;